import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import {
  ComboBoxComponent,
  ComboBoxModule,
  DropDownListComponent,
  DropDownListModule,
  MultiSelectComponent,
  MultiSelectModule,
  PopupSettings,
  PreventableEvent,
  VirtualizationSettings,
} from '@progress/kendo-angular-dropdowns';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import { QPointShowIfTruncatedDirective } from '@qpoint/layout';
import { QPointLoadingIndicatorComponent } from '@qpoint/loading';
import { firstValueFrom, Observable, of, timer } from 'rxjs';

import { QPointSuggestListGroupTemplateDirective } from './q-point-suggest-list-group-template.directive';
import { QPointSuggestlistDetailTemplateDirective } from './qpoint-suggestlist-detail-template.directive';
import { QPointSuggestlistNoDataTemplateDirective } from './qpoint-suggestlist-no-data-template.directive';
import { QPointSuggestlistValueTemplateDirective } from './qpoint-suggestlist-value-template.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum DropDownVariant {
  ComboBox,
  MultiSelect,
  DropDownList
}

@Component({
    selector: 'qpoint-suggestlist',
    templateUrl: './suggestlist.component.html',
    styleUrls: ['./suggestlist.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        ComboBoxModule,
        FormsModule,
        RouterModule,
        TranslateModule,
        ReactiveFormsModule,
        MultiSelectModule,
        DropDownListModule,
        QPointLoadingIndicatorComponent,
        ButtonModule,
        QPointShowIfTruncatedDirective,
        NgbTooltip
    ]
})
export class QPointSuggestlistComponent implements OnInit, OnChanges, ControlValueAccessor, AfterViewInit {
  public isString = (value) => typeof value === 'string';

  private readonly defaultMaxPopupWidth = '600px';
  private destroyRef = inject(DestroyRef);
  chevronDownIcon = chevronDownIcon;
  public maxPopupWidthPxSignal = signal<string>(this.defaultMaxPopupWidth);
  private filterValue: any = "";

  @ViewChild('kendoComboBox')
  private kendoComboxBox: ComboBoxComponent;
  @ViewChild(MultiSelectComponent)
  private kendoMultiSelect: MultiSelectComponent;
  @ViewChild('kendoDropdown')
  private kendoDropdown: DropDownListComponent;

  /***
   * OnChange and OnTouched PlaceHolders for ngValueAccessor Callback registration
   */
  public onChange;
  public onTouched;
  @Input()
  required: boolean;
  @Input()
  public dropdownListClearable = false;
  @Input()
  disabled = false;
  @Input()
  readonly = false;
  @Input()
  isLoading = false;
  @Input()
  data: any[];
  @Input()
  isDataGrouped = false; // is used if custom values are allowed and data input is grouped
  @Input()
  value: any;
  @Input()
  textField = 'name';
  @Input()
  valueField = 'id';
  @Input()
  valuePrimitive = true;
  @Input()
  allowCustom: boolean;
  @Input()
  filterable = true;
  @Input()
  placeholder: string;
  @Input()
  dropDownVariant = DropDownVariant.ComboBox;
  @Input()
  popupSettings: PopupSettings;
  @Input()
  public set maxPopupWidthPx(value: number) {
    if (value) {
      this.maxPopupWidthPxSignal.set(value + 'px');
    } else {
      this.maxPopupWidthPxSignal.set(this.defaultMaxPopupWidth);
    }
  }
  @Input()
  hasMoreEntries = false;
  @Input()
  virtualizationSettings: VirtualizationSettings;
  @Input()
  dropdownDefaultItem: any;
  @Input()
  preselectSingleItem = false; 
  @ContentChild(QPointSuggestlistDetailTemplateDirective, { read: TemplateRef })
  detailTemplate;
  @ContentChild(QPointSuggestlistValueTemplateDirective, { read: TemplateRef })
  valueTemplate;
  @ContentChild(QPointSuggestListGroupTemplateDirective, { read: TemplateRef })
  groupTemplate;
  @ContentChild(QPointSuggestlistNoDataTemplateDirective, { static: true, read: TemplateRef }) noDataTemplate: TemplateRef<any>;
  @Output()
  touch: EventEmitter<any> = new EventEmitter();
  @Output()
  valueChange: EventEmitter<any> = new EventEmitter();
  @Output()
  filterChange: EventEmitter<any> = new EventEmitter();
  @Output()
  loadMoreEntries: EventEmitter<boolean> = new EventEmitter();
  @Output()
  close: EventEmitter<PreventableEvent> = new EventEmitter();

  @HostBinding('class.readonly') get isReadonly() {
    return this.readonly;
  }

  public isFocused = false;
  public lastEnteredValue: string = '';
  public dropDownVariants = DropDownVariant;
  private delay = (function () {
    let timerDelay = null;
    return function (callback: any, ms: any) {
      clearTimeout(timerDelay);
      timerDelay = setTimeout(callback, ms);
    };
  })();
  @Input()
  itemDisabled = f => false
  @Input()
  public valueNormalizer: any = (text: Observable<string>) => text

  // Fix validation bubbling to kendo-suggestlist, based on: https://stackoverflow.com/a/61857086
  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
  }

  get asFormControl(): UntypedFormControl | null {
    return (this.ngControl?.control as UntypedFormControl) || null;
  }

  get clearButtonVisible(): boolean {
    return !this.readonly && !this.disabled && this.dropdownListClearable && this.value != null
  }

  ngAfterViewInit() {
    // Set initial control state to pristine
    if (this.ngControl != null && this.ngControl.untouched) {
      this.ngControl?.control?.markAsPristine();
    }

    if (this.dropDownVariant === DropDownVariant.ComboBox) {
      this.kendoMultiSelect.handleNavigate = () => { };
    }
  }

  dataContainsSingleCustomValue = false;
  private addCustomValueToListIfNecessary() {
    if (this.dropDownVariant === DropDownVariant.ComboBox
      && this.allowCustom
      && this.value
      && !this.isDataGrouped) {
      if (!this.data.find(d => d[this.valueField] === this.value)) {
        this.data = [...this.data, { [this.textField]: this.value, [this.valueField]: this.value, customValue: true }]
        if (this.data.length === 1) {
          this.dataContainsSingleCustomValue = true;
        }
      }
    }
  }

  ngOnInit() {
    this.dropDownVariant = this.dropDownVariant ? this.dropDownVariant : DropDownVariant.ComboBox;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.preselectSingleItem && changes.data && this.filterValue?.length === 0) {
      timer(150)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          const currentDataFromChanges = changes['data'].currentValue;
          const currentDataArray = currentDataFromChanges && currentDataFromChanges[0] && currentDataFromChanges[0]['items'] && currentDataFromChanges[0]['items'].length > 0 ? currentDataFromChanges[0]['items'] : currentDataFromChanges;

          if (currentDataArray && currentDataArray.length === 1 && !this.value) {
            let elementToChange = null;
            if (!this.valuePrimitive) {
              elementToChange = currentDataArray[0];
            } else {
              const currentValueElement = currentDataArray[0];
              if (currentValueElement[this.valueField]) {
                if (!this.itemDisabled({dataItem: currentValueElement})) {
                  elementToChange = currentValueElement[this.valueField];
                }
              } else {
                elementToChange = currentValueElement;
              }
            }
            if (elementToChange) {
              this.valueChanged(elementToChange);
            }
          }
        });
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Is used whenever the control receives a touch event
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  valueChanged(value: any) {
    this.value = value;
    this.valueChange.emit(value);
    if (this.onChange) {
      this.onChange(value);
    }
  }

  comboboxValueChanged(value: any) {
    value = value && value.length > 0 ? value[value.length - 1] : null;
    this.valueChanged(value);
    this.lastEnteredValue = "";
  }

  async onTouch() {
    this.isFocused = false;
    this.touch.emit();

    if (this.allowCustom && this.lastEnteredValue) {
      const customValue = this.valueNormalizer ? await firstValueFrom(this.valueNormalizer(of(this.lastEnteredValue))) : this.lastEnteredValue;
      this.writeValue(customValue);
      this.valueChanged(customValue);
      this.lastEnteredValue = '';
    }

    if (this.onTouched) {
      this.onTouched();
    }
  }

  onFilterChange(value: any) {
    if (this.allowCustom && value) {
      this.lastEnteredValue = value;
    }
    this.filterValue = value;
    this.delay(() => {
      this.filterChange.emit(this.filterValue);
    }, 200);
  }

  public onFocus() {
    this.isFocused = true;
  }

  public onClose(event: PreventableEvent) {
    this.close.emit(event);
  }

  public handleComboBoxChangeEvent(_: Event) {
    if (this.kendoMultiSelect && !this.disabled && !this.readonly) {
      this.kendoMultiSelect.clearFilter()
    }
  }

  public footerClick() {
    this.loadMoreEntries.emit(true);
  }

  writeValue(obj: any): void {
    this.value = obj;
    this.addCustomValueToListIfNecessary();
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  resetValue($event: Event) {
    this.valueChanged(null);
    $event.stopPropagation();
    $event.preventDefault();
  }

  public focus() {
    if (this.kendoDropdown) {
      this.kendoDropdown.focus();
    }
    if (this.kendoComboxBox) {
      this.kendoComboxBox.focus();
    }
    if (this.kendoMultiSelect) {
      this.kendoMultiSelect.focus();
    }
  }

  handleComboBoxKeyEvent(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (this.data.length === 0) return;
    this.kendoMultiSelect.togglePopup(true);
    let indexToSelect = 0;
    let data = this.kendoMultiSelect.data;
    if (this.kendoMultiSelect.data[0].header) {
      data = this.kendoMultiSelect.data.filter(d => !d.header).map(d => d.value);
    }
    if (this.value) {
      if (this.valuePrimitive === true) {
        indexToSelect = data.indexOf(data.find(v => v[this.valueField] == this.value)) ?? 0;
      } else if (this.valueField) {
        indexToSelect = data.indexOf(data.find(v => v[this.valueField] == this.value[this.valueField])) ?? 0;
      } else {
        indexToSelect = data.indexOf(data.find(v => v == this.value)) ?? 0;
      }
      indexToSelect += keyboardEvent.key === 'ArrowDown' ? 1 : -1;
      if (indexToSelect < 0) indexToSelect = 0;
    }

    if (data[indexToSelect]) {
      this.valueChanged(this.valuePrimitive === true ? data[indexToSelect][this.valueField] : data[indexToSelect]);
    }
  }

  handleComboBoxEnterEvent() {
    if (this.kendoMultiSelect) {
      this.kendoMultiSelect.togglePopup(false);
      this.lastEnteredValue = '';
    }
  }

  public handleComboBoxBackspaceEvent() {
    if (!this.filterable && this.value) {
      this.kendoMultiSelect.clearAll();
    } else if (this.filterable && (!this.filterValue || this.filterValue === '')) {
      this.valueChanged(null);
    }
  }

  dropdownAboutToOpen($event: PreventableEvent) {
    if (this.readonly) {
      $event.preventDefault();
    }
  }

  public openMultiselectDropdown() {
    if (this.kendoMultiSelect && !this.disabled && !this.readonly) {
      this.kendoMultiSelect.toggle();
      this.kendoMultiSelect.focus();
    }
  }

}
